import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as gameApi from '../api/gameApi';
import {
  GameAddViewModel,
  GameFranchiseAddViewModel,
  GameFranchiseModel,
  GameItemDto,
} from '../types/swagger-dtos/GamesDtos';

export const fetchAllGames = createAsyncThunk<GameItemDto[], void>(
  'games/fetchAll',
  async (_, { dispatch }) => {
    const games = await gameApi.getAllGames();

    dispatch(setGames(games));

    return games;
  }
);

export const archiveGame = createAsyncThunk<
  string,
  {
    id: string;
    lockSignOff: boolean;
  }
>('games/archive', async ({ id, lockSignOff }, { dispatch }) => {
  await gameApi.archiveGame(id, lockSignOff);

  dispatch(archiveGameAction(id));

  return id;
});

export const saveGame = createAsyncThunk<
  GameItemDto,
  GameAddViewModel & { id?: string }
>('games/save', async (game, { dispatch }) => {
  const savedGame = await gameApi.saveGame(game);

  dispatch(game.id ? updateGame(savedGame) : createGame(savedGame));

  return savedGame;
});

export const unarchiveGame = createAsyncThunk<
  void,
  { id: string; restorePendingRequests: boolean }
>('games/unarchive', async ({ id, restorePendingRequests }, { dispatch }) => {
  await gameApi.unarchiveGame(id, restorePendingRequests);

  dispatch(unarchiveGameAction(id));
});

const gamesSlice = createSlice({
  name: 'games',
  initialState: null as GameItemDto[] | null,
  reducers: {
    setGames: (_, action: PayloadAction<GameItemDto[]>) => {
      return action.payload;
    },
    updateGame: (state, action: PayloadAction<GameItemDto>) => {
      if (state === null) return state;

      return state.map((game) =>
        game.id === action.payload.id ? action.payload : game
      );
    },
    archiveGameAction: (state, action: PayloadAction<string>) => {
      if (state === null) return state;

      return state.map((game) =>
        game.id === action.payload ? { ...game, isArchived: true } : game
      );
    },
    unarchiveGameAction: (state, action: PayloadAction<string>) => {
      if (state === null) return state;

      return state.map((game) =>
        game.id === action.payload ? { ...game, isArchived: false } : game
      );
    },
    createGame: (state, action: PayloadAction<GameItemDto>) => {
      if (state === null) return state;

      return [...state, action.payload];
    },
  },
});

const gamesFranchisesSlice = createSlice({
  name: 'gameFranchises',
  initialState: null as GameFranchiseModel[] | null,
  reducers: {
    setGameFranchises: (_, action: PayloadAction<GameFranchiseModel[]>) => {
      return action.payload;
    },
    createGameFranchise: (state, action: PayloadAction<GameFranchiseModel>) => {
      if (state === null) return state;
      return [...state, action.payload];
    },
    updateGameFranchise: (state, action: PayloadAction<GameFranchiseModel>) => {
      if (state === null) return state;
      return state.map((franchise) =>
        franchise.id === action.payload.id ? action.payload : franchise
      );
    },
    deleteGameFranchiseAction: (state, action: PayloadAction<string>) => {
      if (state === null) return state;
      return state.filter((franchise) => franchise.id !== action.payload);
    },
  },
});

export const saveGameFranchise = createAsyncThunk<
  GameFranchiseModel,
  GameFranchiseAddViewModel & { id?: string }
>('games/franchise/save', async (franchise, { dispatch }) => {
  const savedGameFranchise = await gameApi.saveGameFranchise(franchise);

  dispatch(
    franchise.id
      ? updateGameFranchise(savedGameFranchise)
      : createGameFranchise(savedGameFranchise)
  );

  return savedGameFranchise;
});

export const fetchGameFranchises = createAsyncThunk<GameFranchiseModel[], void>(
  'games/franchise',
  async (_, { dispatch }) => {
    const franchises = await gameApi.getAllGameFranchises();
    dispatch(setGameFranchises(franchises));

    return franchises;
  }
);

export const deleteGameFranchise = createAsyncThunk<void, string>(
  'games/franchise/delete',
  async (id, { dispatch }) => {
    await gameApi.deleteGameFranchise(id);
    dispatch(gamesFranchisesSlice.actions.deleteGameFranchiseAction(id));
  }
);

export const {
  setGames,
  updateGame,
  createGame,
  archiveGameAction,
  unarchiveGameAction,
} = gamesSlice.actions;

export const {
  createGameFranchise,
  updateGameFranchise,
  setGameFranchises,
  deleteGameFranchiseAction,
} = gamesFranchisesSlice.actions;

export const games = gamesSlice.reducer;
export const gameFranchises = gamesFranchisesSlice.reducer;
